import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  IconButton,
  MenuItem,
  useTheme,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import { CREATE_EMPLOYEE } from "../../../../../../apis/organization-apis/people-apis/peopleApis";
import ConfirmSnackbar from "../../../../../../components/Snackbar/ConfirmSnackbar";
import { Slide } from "@mui/material";
import { MasterLookupItemsType, AccountCategoryKey, ValidationMessages, EmploymentStatus, EmploymentStatusLabel } from "../../../../../../utils/enums";
import DatePicker from "../../../../../../components/DatePicker/DatePicker";

const TransitionComponent = (props) => {
  return <Slide {...props} direction="left" />;
};

const AddEmployeeDialog = ({ open, onClose, refetch, onBack }) => {
  const theme = useTheme();
  const [createEmployee, { loading }] = useMutation(CREATE_EMPLOYEE);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    email: "",
    start_date: "",
    gender: "",
    employment_status: EmploymentStatusLabel.ACTIVE,
    termination_date: "",
    role: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.employee_id?.trim()) newErrors.employee_id = ValidationMessages.REQUIRED;
    if (!formData.first_name?.trim()) newErrors.first_name = ValidationMessages.REQUIRED;
    if (!formData.last_name?.trim()) newErrors.last_name = ValidationMessages.REQUIRED;
    if (!formData.email?.trim()) {
      newErrors.email = ValidationMessages.REQUIRED;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email";
    }
    if (!formData.start_date) newErrors.start_date = ValidationMessages.REQUIRED;
    if (formData.employment_status === EmploymentStatusLabel.OFFBOARDED_USER && !formData.termination_date) {
      newErrors.termination_date = "Termination date is required for offboarded users";
    }
    if (formData.start_date && formData.termination_date) {
      const startDate = new Date(formData.start_date);
      const terminationDate = new Date(formData.termination_date);
      if (startDate > terminationDate) {
        newErrors.termination_date = "Termination date cannot be before start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    // Map employment_status to employee_type and employee_key
    let employee_type, employee_key;
    const employment_status_value = 
      formData.employment_status === EmploymentStatusLabel.ACTIVE
        ? EmploymentStatus.ACTIVE
        : formData.employment_status === EmploymentStatusLabel.SERVICE_ACCOUNT
        ? EmploymentStatus.INACTIVE
        : EmploymentStatus.TERMINATED;

    if (employment_status_value === EmploymentStatus.ACTIVE) {
      employee_type = MasterLookupItemsType.IN_SCOPE_USER_CATEGORY;
      employee_key = AccountCategoryKey.ACTIVE_USER;
    } else if (employment_status_value === EmploymentStatus.INACTIVE) {
      employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
      employee_key = AccountCategoryKey.NON_PERSON;
    } else if (employment_status_value === EmploymentStatus.TERMINATED) {
      employee_type = MasterLookupItemsType.NOT_IN_SCOPE_USER_CATEGORY;
      employee_key = AccountCategoryKey.OFFBOARDED_USER;
    }

    try {
      const employments = [
        {
          id: formData.employee_id.trim(),
          job_title: formData.role?.trim() || null,
          effective_date: formData.start_date,
          employment_type: "full-time",
        },
      ];

      const response = await createEmployee({
        variables: {
          input: {
            employee_id: formData.employee_id.trim(),
            first_name: formData.first_name.trim(),
            middle_name: formData.middle_name?.trim() || null,
            last_name: formData.last_name.trim(),
            name: `${formData.first_name.trim()} ${formData.middle_name?.trim() || ""} ${formData.last_name.trim()}`.trim(),
            email: formData.email.trim(),
            start_date: formData.start_date,
            gender: formData.gender || null,
            employment_status: employment_status_value,
            termination_date: (() => {
              const dateValue = formData.termination_date;
              if (!dateValue) return null;
              return dateValue.split('T')[0];
            })(),
            employee_type,
            employee_key,
            employments,
          },
        },
      });

      if (response?.data?.createEmployee?.success) {
        setSnackbarInfo({
          message: response.data.createEmployee.message,
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
        handleClose();
        if (refetch) refetch();
      } else {
        setSnackbarInfo({
          message: response?.data?.createEmployee?.message || "Failed to create employee",
          vertical: "top",
          horizontal: "right",
          Transition: TransitionComponent,
        });
      }
    } catch (error) {
      console.error("Error creating employee:", error);
      setSnackbarInfo({
        message: error?.message || "Failed to create employee",
        vertical: "top",
        horizontal: "right",
        Transition: TransitionComponent,
      });
    }
  };

  const handleClose = () => {
    setFormData({
      employee_id: "",
      first_name: "",
      middle_name: "",
      last_name: "",
      email: "",
      start_date: "",
      gender: "",
      employment_status: EmploymentStatusLabel.ACTIVE,
      termination_date: "",
      role: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            pb: 2,
            pt: 2.5,
            px: 3,
            position: "relative",
          }}
        >
          Add Employee
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ 
              position: "absolute", 
              right: 12, 
              top: 12,
              color: theme.customColors.text.secondary,
              "&:hover": {
                backgroundColor: theme.customColors.background.hover,
                color: theme.customColors.text.strong,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          dividers
          sx={{
            px: 3,
            py: 3,
            "&.MuiDialogContent-root": {
              paddingTop: 1,
            },
          }}
        >
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong ?? "#374151",
                  mb: 0.75,
                }}
              >
                Employee ID *
              </Typography>
              <TextField
                fullWidth
                value={formData.employee_id}
                onChange={handleChange("employee_id")}
                error={!!errors.employee_id}
                helperText={errors.employee_id}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong ?? "#374151",
                  mb: 0.75,
                }}
              >
                First Name *
              </Typography>
              <TextField
                fullWidth
                value={formData.first_name}
                onChange={handleChange("first_name")}
                error={!!errors.first_name}
                helperText={errors.first_name}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong ?? "#374151",
                  mb: 0.75,
                }}
              >
                Middle Name
              </Typography>
              <TextField
                fullWidth
                value={formData.middle_name}
                onChange={handleChange("middle_name")}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong ?? "#374151",
                  mb: 0.75,
                }}
              >
                Last Name *
              </Typography>
              <TextField
                fullWidth
                value={formData.last_name}
                onChange={handleChange("last_name")}
                error={!!errors.last_name}
                helperText={errors.last_name}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong ?? "#374151",
                  mb: 0.75,
                }}
              >
                Email *
              </Typography>
              <TextField
                fullWidth
                type="email"
                value={formData.email}
                onChange={handleChange("email")}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                fullWidth
                label="Start Date"
                required
                value={formData.start_date}
                onChange={(date) => handleChange("start_date")({ target: { value: date } })}
                error={!!errors.start_date}
                helperText={errors.start_date}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong ?? "#374151",
                  mb: 0.75,
                }}
              >
                Gender
              </Typography>
              <TextField
                fullWidth
                select
                value={formData.gender}
                onChange={handleChange("gender")}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="M">Male</MenuItem>
                <MenuItem value="F">Female</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong ?? "#374151",
                  mb: 0.75,
                }}
              >
                Employment Status
              </Typography>
              <TextField
                fullWidth
                select
                value={formData.employment_status}
                onChange={handleChange("employment_status")}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              >
                <MenuItem value={EmploymentStatusLabel.ACTIVE}>Active</MenuItem>
                <MenuItem value={EmploymentStatusLabel.SERVICE_ACCOUNT}>Service account</MenuItem>
                <MenuItem value={EmploymentStatusLabel.OFFBOARDED_USER}>Offboarded user</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography
                component="label"
                sx={{
                  display: "block",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: theme.customColors?.text?.strong ?? "#374151",
                  mb: 0.75,
                }}
              >
                Role
              </Typography>
              <TextField
                fullWidth
                value={formData.role}
                onChange={handleChange("role")}
                placeholder="e.g., CEO, SDE1"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    "&:hover fieldset": {
                      borderColor: theme.customColors.border.strong,
                    },
                  },
                }}
              />
            </Grid>
            {formData.employment_status === EmploymentStatusLabel.OFFBOARDED_USER && (
              <Grid item xs={12} sm={6}>
                <DatePicker
                  fullWidth
                  label="Termination Date"
                  required
                  value={formData.termination_date}
                  onChange={(date) => handleChange("termination_date")({ target: { value: date } })}
                  error={!!errors.termination_date}
                  helperText={errors.termination_date}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&:hover fieldset": {
                        borderColor: theme.customColors.border.strong,
                      },
                    },
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 3,
            pb: 2.5,
            pt: 2,
            background: theme.customColors.background.cardHeader,
            borderTop: `1px solid ${theme.customColors.border.main}`,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.875,
              borderRadius: 1,
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.strong,
              "&:hover": {
                borderColor: theme.customColors.border.strong,
                backgroundColor: theme.customColors.background.hover,
                color: theme.customColors.text.strong,
              },
            }}
          >
            Cancel
          </Button>
          {onBack && (
            <Button
              onClick={onBack}
              variant="outlined"
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.875rem",
                px: 2.5,
                py: 0.875,
                borderRadius: 1,
                borderColor: theme.customColors.border.main,
                color: theme.customColors.text.strong,
                "&:hover": {
                  borderColor: theme.customColors.border.strong,
                  backgroundColor: theme.customColors.background.hover,
                  color: theme.customColors.text.strong,
                },
              }}
            >
              Back
            </Button>
          )}
          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            loading={loading}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.875,
              borderRadius: 1,
              bgcolor: theme.customColors.button.hover,
              color: theme.customColors.button.text,
              "&:hover": {
                bgcolor: theme.customColors.button.primary,
              },
              "&:disabled": {
                bgcolor: theme.customColors.background.light,
                color: theme.customColors.text.disabled,
              },
            }}
          >
            Add Employee
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default AddEmployeeDialog;

